import { inject, Injectable } from '@angular/core';
import { BxNotificationService } from '@beyonnex/bx-angular-ui';
import { TranslateService } from '@ngx-translate/core';
import type { NotificationOptions } from './notifications.service.models';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly bxNotification = inject(BxNotificationService);
  private readonly translate = inject(TranslateService);

  addNotification({ title, supportingTitle, type, traceparent, persistent = !!traceparent }: NotificationOptions): string {
    return this.bxNotification.show({
      title,
      type,
      supportingTitle: traceparent ? this.withTrace(supportingTitle, traceparent) : supportingTitle,
      duration: persistent ? null : undefined,
      closeAriaLabel: this.translate.instant('system.aria.close'),
    });
  }

  addSuccessNotification(title: string, message: string): string {
    return this.addNotification({ title, supportingTitle: message, type: 'success' });
  }

  addWarningNotification(title: string, message: string): string {
    return this.addNotification({ title, supportingTitle: message, type: 'warning' });
  }

  addErrorNotification(title: string, error: string, traceparent?: string): string {
    return this.addNotification({ title, supportingTitle: error, type: 'error', traceparent });
  }

  addPersistentErrorNotification(title: string, error: string, traceparent?: string): string {
    return this.addNotification({ title, supportingTitle: error, type: 'error', traceparent, persistent: true });
  }

  addGenericErrorNotification(error: string, traceparent?: string): string {
    return this.addNotification({
      title: this.translate.instant('system.errors.somethingWentWrong'),
      supportingTitle: error,
      type: 'error',
      traceparent,
    });
  }

  remove(id: string): void {
    this.bxNotification.dismiss(id);
  }

  // The card renders string content as HTML; `wrap-normal` makes the id break at its dashes instead of mid-token.
  private withTrace(message: string | undefined, traceparent: string): string {
    const hint = this.translate.instant('system.errors.traceparentHint');
    const trace = `<span class="text-text-brand-tertiary-600 text-xs wrap-normal">${traceparent}</span>`;

    return [message, hint, trace].filter(Boolean).join('<br>');
  }
}
